import { AfterViewInit, Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule, NgForm } from '@angular/forms'
import { HttpParams } from '@angular/common/http'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faCircleQuestion } from '@fortawesome/free-regular-svg-icons'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { QuillEditorComponent } from 'ngx-quill'
import { debounceTime, skip } from 'rxjs/operators'

import { CompanyQuarter, LEADERSHIP_L10_MEETING_ID, ListItem, RepositoryModal, Rock, RockDueDateHistory, quillLinkHandler } from '../../shared'
import { RocksComponent } from './rocks.component'

type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error'

@Component({
    templateUrl: './rock.component.html',
    standalone: true,
    imports: [FormsModule, WjInputModule, FaIconComponent, QuillEditorComponent],
})
export class RockComponent extends RepositoryModal<Rock> implements OnInit, AfterViewInit {
    private destroyRef = inject(DestroyRef)

    @ViewChild('form') form!: NgForm

    faQuestionCircle = faCircleQuestion
    userList: ListItem<string>[] = []
    meetingsList: ListItem<string>[] = []
    statusList: ListItem<number>[] = []
    typeList: ListItem<number>[] = []
    boolList: ListItem<boolean>[] = [new ListItem(true, 'Yes'), new ListItem(false, 'No')]
    quarter: CompanyQuarter | null = null
    dueDateHistories: RockDueDateHistory[] = []
    autoSaveStatus: AutoSaveStatus = 'idle'
    isLeadershipMeeting = false

    editorModules = {
        toolbar: {
            container: [
                ['bold', 'italic', 'underline'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ header: [1, 2, 3, false] }],
                ['link'],
            ],
            handlers: { link: quillLinkHandler },
        },
        clipboard: {
            matchVisual: false,
        },
    }

    ngOnInit() {
        const parentComponent = this.editOptions.initializeParams as RocksComponent
        this.userList = parentComponent.allActiveUserList.filter(listItem => parentComponent.meeting()?.userIds.includes(listItem.value))
        this.meetingsList = parentComponent.meetings().map(meeting => new ListItem<string>(meeting.id, meeting.name))
        this.statusList = parentComponent.statusList
        this.typeList = parentComponent.typeList
        this.quarter = parentComponent.quarter
        this.isLeadershipMeeting = parentComponent.meetingId()?.toLowerCase() === LEADERSHIP_L10_MEETING_ID.toLowerCase()

        if (this.isAdding) {
            // Clear the checked state for all users and meetings
            this.userList.forEach(user => (user.$checked = false))
            this.meetingsList.forEach(meeting => (meeting.$checked = false))

            this.userList.forEach(user => {
                if (user.value === this.services.authentication.userId()) {
                    user.$checked = true
                }
            })

            const currentMeetingId = parentComponent.meetingId()
            this.meetingsList.forEach(meeting => {
                if (meeting.value === currentMeetingId) {
                    meeting.$checked = true
                }
            })

            if (this.quarter)
                this.target.dueDate = this.quarter.endDate // Default to the end of the current company quarter
            else this.target.dueDate = new Date().addDays(7)

            this.target.status = 1 // Default to On Track
            this.target.includeInOneYearGoals = false
        } else {
            this.meetingsList.forEach(meeting => {
                if (this.target.meetingIds.includes(meeting.value)) {
                    meeting.$checked = true
                }
            })
            this.loadDueDateHistory()
        }
    }

    ngAfterViewInit() {
        if (this.isAdding || !this.form?.form) {
            return
        }

        this.form.form.valueChanges.pipe(debounceTime(800), skip(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.autoSave()
        })
    }

    formatDate(value: Date | null | undefined): string {
        if (!value) {
            return '—'
        }
        return new Date(value).toLocaleDateString()
    }

    private loadDueDateHistory() {
        const params = new HttpParams().set('rockId', this.target.id)
        this.services.data.rockDueDateHistories.queryAllByAction('GetByRockId', params).subscribe({
            next: histories => {
                this.dueDateHistories = histories
            },
        })
    }

    private syncMeetingIdsFromForm() {
        this.target.meetingIds = this.meetingsList.filter(meeting => meeting.$checked).map(meeting => meeting.value)
    }

    private autoSave() {
        if (!this.target.name) {
            return
        }

        this.syncMeetingIdsFromForm()
        this.autoSaveStatus = 'saving'

        this.services.data.rocks.edit(this.target).subscribe({
            next: () => {
                this.autoSaveStatus = 'saved'
                this.loadDueDateHistory()
                window.setTimeout(() => {
                    if (this.autoSaveStatus === 'saved') {
                        this.autoSaveStatus = 'idle'
                    }
                }, 2000)
            },
            error: () => {
                this.autoSaveStatus = 'error'
            },
        })
    }

    protected onBeforeSave(): boolean {
        if (!this.target.name) {
            this.services.notification.warning('ROCKS name is required')
            return false
        }

        this.syncMeetingIdsFromForm()
        if (this.isAdding) {
            this.target.userIds = this.userList.filter(user => user.$checked).map(user => user.value)
            if (this.target.userIds.length === 0) {
                this.services.notification.warning('Please select at least one user to assign to the To-Do.')
                return false
            }

            // Send an empty Guid, since the userId is a required field, but not used when adding a new To-Do
            this.target.userId = this.services.utility.emptyGuid()
        }

        return true
    }
}
